import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";

import { AboutTab, EntitySkeleton, FlexCol, FlexRow, ProfileHeader, TweetsTab, BoltPlayer } from "../../components";
import { ANALYTICS_TRACK_TYPE, CHANNEL_TYPE, PROFILE_MENU_ITEM, RECEIVER_TYPE } from "../../types";
import { Popup } from "../../utils";
import { channelSelector } from "../../store/channel/selector";
import { streamDataSelector } from "../../store/streamer/selector";
import { setStreamDataAction } from "../../store/streamer/actions";
import { useImmersivePanelLayoutContext } from "../../layouts";
import { defaultChatContainerId } from "../../constants";
import { useAnalyticsTracker } from "../../hooks";
import { EpisodesCarousel } from "../../components/Vod/EpisodesCarousel";

import { ProductSearchOverlay } from "./ProductSearchOverlay";
import { ProductSearchButton } from "./ProductSearchButton";
import { ProductSearchSidebar } from "./ProductSearchSidebar";
import { authSelector } from "../../store/auth/selector";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;

export const ChannelPage = () => {
  const dispatch = useDispatch();

  const { updateContext: updateImmersivePanelLayout } = useImmersivePanelLayoutContext();

  const { boltId } = useParams();


  const [isProductSearchMode, setIsProductSearchMode] = useState(false);
  const [frozenFrame, setFrozenFrame] = useState<string | null>(null);
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<string | null>(null);
  const [googleLensUrl, setGoogleLensUrl] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const playerContainerRef = useRef<HTMLDivElement>(null);

  const { user: { id: currentUserId = "" } = {} } = useSelector(authSelector);

  const channelFromPath = useMemo(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";          // "" if not present
  }, []);
    
  const handleProductSearchClick = () => {
    // Find the video element and cast it to HTMLVideoElement
    const videoElement = document.querySelector('video#player') as HTMLVideoElement || 
                        document.querySelector('video') as HTMLVideoElement;
    
    if (videoElement && videoElement.videoWidth > 0) {
      // Capture current frame
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(videoElement, 0, 0);
      
      const frameData = canvas.toDataURL('image/png');
      setFrozenFrame(frameData);
      setIsProductSearchMode(true);
    } else {
      console.warn('Video element not ready or not found');
    }
  };


const handleSelectionComplete = async (selectedImageData: string) => {
  setSelectedImage(selectedImageData);
  setIsProductSearchMode(false);
  setFrozenFrame(null);
  setShowSidebar(true);
  setIsSearchingProduct(true);

  try {
    const response = await fetch(`${AI_API_URL}/search-product`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ screenshot: selectedImageData, channel: channelFromPath, user_id: currentUserId}),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 'success') {
      setGoogleLensUrl(data.google_lens_url);
      setSearchResults(null);
    } else {
      throw new Error('Failed to get search results');
    }
  } catch (err) {
    console.error('Failed to search product:', err);
  } finally {
    setIsSearchingProduct(false);
  }
};



  const {
    channel: { data: channel },
  } = useSelector(channelSelector);

  const { url, isLive, title } = useSelector(streamDataSelector);

  useEffect(() => {
    if (url) {
      console.log("⏩ HLS manifest URL for STT:", url);
      console.log(AI_API_URL );
    }
  }, [url]);

  const [currentTab, setCurrentTab] = useState(PROFILE_MENU_ITEM.about);
  const [isSideChatBoxOpen, setIsSideChatBoxOpen] = useState(true);

  useAnalyticsTracker({ entityId: channel?.id!, type: ANALYTICS_TRACK_TYPE.CHANNEL });

  useEffect(() => {
    updateImmersivePanelLayout({
      chatProps: {
        containerId: defaultChatContainerId,
        channelId: channel?.id || "",
        channelType: CHANNEL_TYPE.CHANNEL,
        receiverName: channel?.title || "",
        receiverType: RECEIVER_TYPE.CHANNEL,
      },
    });

    if (channel?.isLive) {
      dispatch(setStreamDataAction({ url: channel?.url, isLive: true, boltId, title: channel.title, isChannel: true }));
    } else {
      dispatch(setStreamDataAction({ url: undefined, isLive: false, boltId, isChannel: true }));
    }
  }, [channel]);

  if (!channel) {
    return (
      <div style={{ minWidth: "100%", position: "absolute", left: 0, top: 0 }}>
        <EntitySkeleton />
      </div>
    );
  }

  const handlePopout = () => {
    Popup.handleVideoPopup(boltId!);
  };
  const [currentEpisode, setCurrentEpisode] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState<any>(0);
  const [currentId, setCurrentId] = useState<any>(null);
  const [vodArray, setVodArray] = useState<any[]>([]);

  useEffect(() => {
    if (channel) {
      if (channel?.vods && channel.vods.length > 0) {
        const channelArray =
          channel?.sourceType === "VOD"
            ? Array.isArray(channel.vods)
              ? channel.vods
              : []
            : [channel, ...(Array.isArray(channel.vods) ? channel.vods : [])];
        setVodArray(channelArray);
        setCurrentId(channel?.id);
      } else {
        setCurrentEpisode(null);
        setVodArray([]);
        setCurrentId(null);
      }
    }
  }, [channel]);

  // useEffect(() => {
  //   if (currentId) {
  //     const filterItem = vodVideos?.find((item: any) => item?.id === currentId);
  //     setCurrentEpisode(filterItem);
  //   }
  // }, [currentId]);

  useEffect(() => {
    if (currentIndex !== null && currentIndex !== undefined && vodArray?.length > 0) {
      setCurrentEpisode(vodArray?.[currentIndex]);
      setCurrentId(vodArray?.[currentIndex]?.id);
    }
  }, [currentIndex, vodArray]);

  const handleVideoEnded = () => {
    if (vodArray?.length > 0) {
      setCurrentIndex((prevIndex: number) => {
        const nextIndex: number = prevIndex + 1;
        return nextIndex === vodArray.length ? 0 : nextIndex;
      });
    }
  };

  return (
    <FlexCol sx={{ width: "100%", height: "auto" }}>
      <FlexRow>
        <FlexCol sx={{ width: "100%", transition: "all 300ms ease", position: "relative" }}>
          <FlexRow
            sx={{
              position: "relative",
              width: "100%",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "flex-start",
              overflow: "hidden",
            }}
          >
            <BoltPlayer
              url={currentEpisode?.hlsUrl ? currentEpisode?.hlsUrl : url}
              title={title}
              isLive={isLive}
              isSideChatBoxOpen={isSideChatBoxOpen}
              handleChatClose={() => setIsSideChatBoxOpen(true)}
              handlePopout={handlePopout}
              isLoop={!currentEpisode?.hlsUrl}
              handleVideoEnded={handleVideoEnded}
            />
            
            {/* Product Search Overlay */}
            {isProductSearchMode && frozenFrame && (
              <ProductSearchOverlay
                frozenFrame={frozenFrame}
                onComplete={handleSelectionComplete}
                onCancel={() => {
                  setIsProductSearchMode(false);
                  setFrozenFrame(null);
                }}
              />
            )}
            
            {/* Product Search Button */}
            <ProductSearchButton onClick={handleProductSearchClick} />

          </FlexRow>

          {vodArray?.length > 1 && (
            <EpisodesCarousel
              data={vodArray}
              setCurrentId={setCurrentId}
              currentId={currentId}
              setCurrentIndex={setCurrentIndex}
            />
          )}

          <Box sx={{ m: 0, mb: 4 }} alignItems="center" justifyContent="center">
            {/* {isXs && <ImmersivePanelDrawer />} */}

            <ProfileHeader
              channel={channel}
              enableChat={!!channel?.enableChat}
              openChatBox={() => {}}
              openGiftBox={() => {}}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              isUser={false}
            />

            <FlexCol sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
              {currentTab === PROFILE_MENU_ITEM.tweets && (
                <TweetsTab id={channel?.id!} advertisement={channel?.advertisement} />
              )}

              {currentTab === PROFILE_MENU_ITEM.about && (
                <AboutTab
                  content={channel?.description}
                  twitter={channel?.twitterId}
                  instagram={channel?.instagramId}
                  website={channel?.website}
                  discord={channel?.discordId}
                />
              )}
            </FlexCol>
          </Box>
        </FlexCol>
      </FlexRow>

      <ProductSearchSidebar
        open={showSidebar}
        onClose={() => setShowSidebar(false)}
        selectedImage={selectedImage}
        isLoading={isSearchingProduct}
        searchResults={searchResults}
        googleLensUrl={googleLensUrl}
      />
    </FlexCol>
  );
};
